import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './T2AgentPanel.css';

const AGMAgentPanel = () => {
  const [activeTab, setActiveTab] = useState('complete');
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploadingKey, setUploadingKey] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const fileInputRef = useRef(null);
  const pendingUploadKeyRef = useRef(null);

  const [chatHistory, setChatHistory] = useState([]);
  const [chatMeta, setChatMeta] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStreaming, setChatStreaming] = useState(false);
  const [chatStream, setChatStream] = useState('');
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);

  const reload = useCallback(() => {
    setLoading(true);
    fetch('/api/opo/agm/workflow')
      .then((r) => r.json())
      .then((d) => setWorkflow(d))
      .catch((e) => console.error('[agm]', e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadChat = useCallback(() => {
    setChatLoading(true);
    fetch('/api/opo/agm/chat/history')
      .then((r) => r.json())
      .then((d) => {
        setChatHistory(d.history || []);
        setChatMeta(d.meta || null);
      })
      .catch(() => setChatMeta(null))
      .finally(() => setChatLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab !== 'chat') return;
    loadChat();
  }, [activeTab, loadChat]);

  useEffect(() => {
    if (activeTab !== 'chat') return;
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, chatStream, activeTab]);

  const triggerUpload = (key) => {
    setActionError(null);
    pendingUploadKeyRef.current = key;
    fileInputRef.current?.click();
  };

  const onSignedFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    const key = pendingUploadKeyRef.current;
    e.target.value = '';
    pendingUploadKeyRef.current = null;
    if (!file || !key) return;
    setUploadingKey(key);
    setActionError(null);
    const fd = new FormData();
    fd.append('file', file);
    fetch(`/api/opo/agm/signed/${encodeURIComponent(key)}/upload`, { method: 'POST', body: fd })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || 'Upload failed')))))
      .then((d) => {
        if (d.completion) setWorkflow((w) => (w ? { ...w, completion: d.completion } : w));
        reload();
      })
      .catch((err) => setActionError(err.message))
      .finally(() => setUploadingKey(null));
  };

  const finalizeAgm = () => {
    setFinalizing(true);
    setActionError(null);
    fetch('/api/opo/agm/finalize', { method: 'POST' })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || 'Finalize failed')))))
      .then((d) => {
        if (d.completion) setWorkflow((w) => (w ? { ...w, completion: d.completion } : w));
        reload();
      })
      .catch((err) => setActionError(err.message))
      .finally(() => setFinalizing(false));
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatStreaming) return;
    setChatInput('');
    setChatStreaming(true);
    setChatStream('');
    setChatHistory((h) => [...h, { role: 'user', content: msg, created_at: new Date().toISOString() }]);
    try {
      const res = await fetch('/api/opo/agm/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Chat failed (${res.status})`);
      }
      const modelHdr = res.headers.get('X-Agm-Chat-Model');
      if (modelHdr) setChatMeta((m) => (m ? { ...m, activeModel: modelHdr } : m));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setChatStream(full);
      }
      setChatHistory((h) => [...h, { role: 'assistant', content: full, created_at: new Date().toISOString() }]);
      setChatStream('');
    } catch (e) {
      setChatStream(`**Error:** ${e.message || 'request failed'}`);
    } finally {
      setChatStreaming(false);
    }
  };

  const onChatKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChat();
    }
  };

  const completion = workflow?.completion;

  return (
    <div className="t2-panel">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        onChange={onSignedFileChange}
      />
      <div className="t2-panel-header">
        <div>
          <h2 className="t2-panel-title">AGM</h2>
          <p className="t2-panel-subtitle">
            First annual general meeting — OptiPrime AI Inc. — planned{' '}
            <strong>{workflow?.meetingDate || '2026-06-04'}</strong>.
          </p>
        </div>
        {completion && (
          <div
            className={`t2-summary-pill${completion.complete ? ' t2-summary-pill--done' : ''}`}
            title={`${completion.signedCount}/${completion.total} signed documents on file`}
          >
            <div className="t2-summary-pct">{completion.complete ? '✓' : `${completion.percent}%`}</div>
            <div className="t2-summary-lbl">{completion.complete ? 'AGM complete' : 'Signed & filed'}</div>
            <div className="t2-summary-crit">
              {completion.signedCount}/{completion.total} signed
            </div>
          </div>
        )}
      </div>

      <div className="t2-tabs" role="tablist">
        {[
          { id: 'complete', label: 'Complete the AGM' },
          { id: 'chat', label: 'Conversation' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`t2-tab${activeTab === t.id ? ' is-active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
        {activeTab === 'chat' && chatMeta?.activeModel && (
          <span className="t2-tab-model" title="Ollama model used for AGM answers">
            {chatMeta.activeModel}
          </span>
        )}
      </div>

      {activeTab === 'complete' && (
        <div className="t2-tab-body">
          {loading && <div className="t2-loading">Loading AGM status…</div>}
          {!loading && completion && (
            <>
              {completion.complete ? (
                <div className="agm-complete-banner agm-complete-banner--done">
                  <div className="agm-complete-banner-title">✓ The first AGM is complete</div>
                  <div className="agm-complete-banner-sub">
                    All {completion.total} signed documents are on file, including the signed minutes in the
                    minute book (<code>compliance/minute_book/agm_2026-06-04.pdf</code>).
                  </div>
                  {!completion.finalized ? (
                    <button
                      type="button"
                      className="t2-btn t2-btn-primary"
                      disabled={finalizing}
                      onClick={finalizeAgm}
                      style={{ marginTop: '0.75rem' }}
                    >
                      {finalizing ? 'Recording…' : 'Finalize & record AGM'}
                    </button>
                  ) : (
                    <div className="agm-complete-banner-sub" style={{ marginTop: '0.5rem' }}>
                      Completion record saved.{' '}
                      {completion.record?.viewUrl && (
                        <a href={completion.record.viewUrl} target="_blank" rel="noopener noreferrer" className="t2-doc-link">
                          View record
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="agm-complete-banner">
                  <div className="agm-complete-banner-title">
                    {completion.signedCount} of {completion.total} signed documents uploaded
                  </div>
                  <div className="agm-complete-banner-sub">
                    {completion.nextUp ? (
                      <>
                        Next: sign and upload{' '}
                        {completion.nextUp.blankPdf ? (
                          <a
                            href={completion.nextUp.blankPdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="t2-doc-link"
                          >
                            <strong>{completion.nextUp.label}</strong>
                          </a>
                        ) : (
                          <strong>{completion.nextUp.label}</strong>
                        )}
                        . Click the document title to view the unsigned PDF; use Upload signed when ready.
                      </>
                    ) : (
                      'Upload each signed document below to complete the AGM.'
                    )}
                  </div>
                </div>
              )}

              {actionError && (
                <div className="t2-send-status t2-send-status--warn" style={{ margin: '0.5rem 0' }}>
                  {actionError}
                </div>
              )}

              <ol className="agm-complete-list">
                {completion.items.map((it) => {
                  const viewHref =
                    it.status === 'signed' && it.signed?.viewUrl ? it.signed.viewUrl : it.blankPdf;
                  const viewTitle =
                    it.status === 'signed'
                      ? 'View signed document (opens in new tab)'
                      : it.postMeeting
                        ? 'View unsigned minutes PDF to sign (opens in new tab)'
                        : 'View unsigned PDF to sign (opens in new tab)';
                  return (
                    <li key={it.key} className={`agm-complete-item agm-complete-item--${it.status}`}>
                      <div className="agm-complete-item-main">
                        <span className="agm-complete-step">{it.status === 'signed' ? '✓' : it.order}</span>
                        <div className="agm-complete-item-text">
                          <div className="agm-complete-item-label">
                            {viewHref ? (
                              <a
                                href={viewHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="agm-complete-item-label-link"
                                title={viewTitle}
                              >
                                {it.label}
                              </a>
                            ) : (
                              it.label
                            )}
                            {it.postMeeting && <span className="agm-signing-badge">After meeting</span>}
                          </div>
                          <div className="agm-complete-item-status">
                            {it.status === 'signed' ? (
                              <>
                                Signed — <code>{it.signedTarget}</code>
                              </>
                            ) : (
                              'Awaiting signed upload — click title to view unsigned PDF'
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="agm-complete-item-actions">
                        <button
                          type="button"
                          className="t2-btn t2-btn-secondary"
                          disabled={uploadingKey === it.key}
                          onClick={() => triggerUpload(it.key)}
                        >
                          {uploadingKey === it.key
                            ? 'Uploading…'
                            : it.status === 'signed'
                              ? 'Replace signed'
                              : 'Upload signed'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </div>
      )}

      {activeTab === 'chat' && (
        <div className="t2-tab-body t2-chat-body agm-chat-body">
          <div className="agm-chat-info">
            <div className="agm-chat-info-title">AGM assistant (local LLM)</div>
            <p className="agm-chat-info-lead">
              Questions are answered by <strong>Ollama</strong> on this server
              {chatMeta?.activeModel ? (
                <>
                  {' '}
                  using <code>{chatMeta.activeModel}</code>
                </>
              ) : (
                ' (loading model…)'
              )}
              . The model receives live AGM status from your filing checklist — not the internet.
            </p>
            {chatMeta && !chatMeta.ollamaReachable && (
              <p className="agm-chat-info-warn">
                Ollama is not reachable at <code>{chatMeta.ollamaHost}</code>. Start Ollama and pull{' '}
                <code>{chatMeta.cascade?.[0]}</code> (or fallback <code>{chatMeta.cascade?.[1]}</code>) before
                sending messages.
              </p>
            )}
            {chatMeta?.contextSources?.length > 0 && (
              <details className="agm-chat-info-details">
                <summary>What the model can see</summary>
                <ul>
                  {chatMeta.contextSources.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </details>
            )}
            {chatMeta?.disclaimer && <p className="agm-chat-info-disclaimer">{chatMeta.disclaimer}</p>}
          </div>

          <div className="t2-chat-messages">
            {chatLoading && <div className="t2-chat-msg assistant">Loading conversation…</div>}
            {!chatLoading && chatHistory.length === 0 && !chatStream && (
              <div className="t2-chat-empty">
                <p>
                  <strong>Ask about the first AGM</strong> — notice, quorum, signing order, minute book, or what
                  to do now that documents are signed.
                </p>
                <ul>
                  <li>Where should signed AGM minutes be filed in the minute book?</li>
                  <li>What did we need Cognify and 1538799 to sign as corporate shareholders?</li>
                  <li>Summarize our AGM completion status and anything still missing.</li>
                  <li>What is the deadline for the first AGM after incorporation?</li>
                </ul>
              </div>
            )}
            {chatHistory.map((m, i) => (
              <div key={i} className={`t2-chat-msg ${m.role}`}>
                <div className="t2-chat-msg-body">
                  {m.role === 'assistant' ? <ReactMarkdown>{m.content}</ReactMarkdown> : m.content}
                </div>
                {m.created_at && (
                  <div className="t2-chat-msg-meta">{new Date(m.created_at).toLocaleString()}</div>
                )}
              </div>
            ))}
            {chatStream && (
              <div className="t2-chat-msg assistant">
                <div className="t2-chat-msg-body">
                  <ReactMarkdown>{chatStream}</ReactMarkdown>
                  {chatStreaming && <span className="t2-chat-cursor">▌</span>}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="t2-chat-input agm-chat-input">
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={onChatKey}
              placeholder="Ask about AGM documents, minute book filing, shareholder signatures, or next steps… (Enter to send, Shift+Enter for new line)"
              disabled={chatStreaming}
              rows={4}
            />
            <button type="button" className="t2-btn-primary" onClick={sendChat} disabled={chatStreaming}>
              {chatStreaming ? 'Thinking…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AGMAgentPanel;
